import {
  UnumAnswerDetailDto,
  UnumApplicantDto,
  UnumDetailFieldDto,
  UnumQAPoliciesDto,
  UnumStateApplicationQuestionDto,
} from 'enrollment/queries';
import { PsNumericTuner, PsRadioTuner, PsTextTuner } from 'stencilSetup';
import {
  UnumDetailFieldStyled,
  UnumDetailInputStyled,
  UnumDetailTextStyled,
} from '../UnumApplicationQuestionsStyled';
import { formatDateString } from 'enrollment/features/Questionnaire/utils';
import { UnumApplicationSurveyResponsesDto } from '../../types';

const NOT_REQUIRED_DETAILS = ['Middle Name:', 'Address2:'];
const REQUIRED_QUESTION_IDS = ['1006-0', '1006-3'];

export const renderUnumApplicationQuestion = (
  question: UnumStateApplicationQuestionDto,
  onChange?: (e: any) => void
) => {
  return (
    <UnumDetailTextStyled className="w-50">
      {renderUnumInput(question, question.AnswerChangable === 'N', onChange)}
    </UnumDetailTextStyled>
  );
};

const renderUnumInput = (
  question: UnumStateApplicationQuestionDto,
  disabled: boolean,
  onChange?: (e: any) => void
) => {
  const type = question.QuestionAnswerType;
  const name = question.QuestionID;
  const value =
    question.QuestionAnswer === typeof Object ? '' : question.QuestionAnswer;
  const helpText = question.InfoTextHelp || '';

  const id = `unum_questions_${name}_`;

  switch (type) {
    case 'YesNo':
      return (
        <>
          <PsRadioTuner
            inputId={id + 'y'}
            style={{ width: '40%' }}
            name={name}
            value="Y"
            label="Yes"
            checked={value === 'Y'}
            onClick={onChange}
            disabled={disabled}
            otherData={[{ attribute: 'data-questiontype', value: 'questions' }]}
          />
          <PsRadioTuner
            inputId={id + 'n'}
            style={{ width: '40%' }}
            name={name}
            value="N"
            label="No"
            checked={value === 'N'}
            onClick={onChange}
            disabled={disabled}
            otherData={[{ attribute: 'data-questiontype', value: 'questions' }]}
          />
        </>
      );
    case 'String':
      return (
        <PsTextTuner
          inputId={id}
          name={name}
          value={value}
          disabled={disabled}
          onBlur={onChange}
          label={helpText}
          otherData={[{ attribute: 'data-questiontype', value: 'questions' }]}
        />
      );
    case 'Integer':
      return (
        <PsNumericTuner
          inputId={id}
          name={name}
          value={String(value)}
          disabled={disabled}
          onBlur={onChange}
          label={helpText}
          otherData={[{ attribute: 'data-questiontype', value: 'questions' }]}
        />
      );
    case 'Height':
      const [feet, inches] =
        value === typeof String ? String(value).split('-') : [0, 0];
      return (
        <>
          <PsTextTuner
            inputId={id + 'feet'}
            name={name}
            value={feet}
            disabled={disabled}
            onBlur={onChange}
            label="ft."
            otherData={[
              { attribute: 'data-questiontype', value: 'questions' },
              { attribute: 'data-isheight', value: 'feet' },
            ]}
          />
          <PsTextTuner
            inputId={id + 'inches'}
            name={name}
            value={inches}
            disabled={disabled}
            onBlur={onChange}
            label="in."
            otherData={[
              { attribute: 'data-questiontype', value: 'questions' },
              { attribute: 'data-isheight', value: 'inches' },
            ]}
          />
        </>
      );
    case 'Weight':
      return (
        <PsTextTuner
          inputId={id}
          name={name}
          value={value}
          disabled={disabled}
          onBlur={onChange}
          label="lbs."
          otherData={[{ attribute: 'data-questiontype', value: 'questions' }]}
        />
      );
    case 'Date':
      return (
        <PsTextTuner
          inputId={id}
          name={name}
          type="date"
          value={formatDateString(String(value))}
          disabled={disabled}
          onBlur={onChange}
          label="mm/dd/yyyy"
          otherData={[{ attribute: 'data-questiontype', value: 'questions' }]}
        />
      );
    case 'Informational':
    case 'InformationalBold':
      return <></>;
    default:
      return 'Question type not supported';
  }
};

const showRequiredAsterisk = (
  field: UnumDetailFieldDto,
  questionId: string
) => {
  return (
    !NOT_REQUIRED_DETAILS.includes(field.DetailFieldName) ||
    REQUIRED_QUESTION_IDS.includes(questionId)
  );
};

export const renderUnumQuestionDetails = (
  detail: UnumAnswerDetailDto,
  questionId: string,
  responses: UnumApplicationSurveyResponsesDto,
  onChange?: (e: any) => void
) => {
  if (responses.questions[questionId] !== detail.DetailTrigger) {
    return null;
  }

  return (
    <UnumDetailFieldStyled data-testid="unum-question-detail">
      {detail.DetailFields.DetailField.map((field, index) => {
        const value = field.DetailFieldValue || '';
        const nameId = questionId;
        const label =
          String(field.DetailFieldName) === '[object Object]'
            ? ''
            : field.DetailFieldName;
        return (
          <UnumDetailFieldStyled>
            <UnumDetailTextStyled>
              {showRequiredAsterisk(field, questionId) && (
                <span aria-hidden={true} role="presentation" className="red">
                  *
                </span>
              )}
              <p>{label}</p>
            </UnumDetailTextStyled>
            <UnumDetailInputStyled>
              <PsTextTuner
                inputId={nameId}
                name={nameId}
                value={value}
                onBlur={onChange}
                label={' '}
                otherData={[
                  {
                    attribute: 'data-questiontype',
                    value: 'question_answer_details',
                  },
                  {
                    attribute: 'data-detailnum',
                    value: `${index + 1}_${label === '' ? '{}' : label}`,
                  },
                ]}
              />
            </UnumDetailInputStyled>
          </UnumDetailFieldStyled>
        );
      })}
    </UnumDetailFieldStyled>
  );
};

export const renderUnumQAPolicies = (
  qid: string,
  policies: UnumQAPoliciesDto
): (string | JSX.Element)[] => {
  const fields = policies.PolicyFieldRequirements;
  return [
    'Insurance Company Name',
    'Policy Number',
    'Monthly Benefit',
    'Elimination Period',
    'Lapsed Date',
  ].map((field) => {
    const key = field.replaceAll(/\W/, '');
    const tmp = field + ': ';
    if (fields[key] !== 'N') {
      return (
        <UnumDetailFieldStyled>
          <p>{tmp}</p>
          <PsTextTuner
            inputId={`unum_qa_policies_${qid}_${key}`}
            name={key}
            value=""
            label=" "
          />
        </UnumDetailFieldStyled>
      );
    } else {
      return tmp;
    }
  });
};

export const isHiddenFollowUp = (
  questions: UnumApplicantDto[],
  responses: UnumApplicationSurveyResponsesDto,
  stateQuestion: UnumStateApplicationQuestionDto,
  questionIndex: number
) => {
  if (!stateQuestion.ParentQuestionID) {
    return false;
  }

  const parentQuestion = questions[
    questionIndex
  ].ProductQuestions.StateApplicationQuestion.find(
    (questionInner) =>
      questionInner.QuestionID === stateQuestion.ParentQuestionID ||
      (questionInner.QuestionGroupID === stateQuestion.ParentQuestionID &&
        questionInner.QuestionGroupID !== undefined)
  );

  if (!parentQuestion) {
    return false;
  }

  const userResponse = responses.questions[stateQuestion.ParentQuestionID];
  if (
    userResponse === parentQuestion.FollowUpQuestionTrigger ||
    (userResponse === parentQuestion.FollowUpQuestionGroupTrigger &&
      parentQuestion.FollowUpQuestionGroupID !== undefined)
  ) {
    return false;
  } else {
    return true;
  }
};

export const initalizeResponses = (applicants: UnumApplicantDto[]) => {
  const retval = {
    questions: {},
    question_answer_details: {},
  } as UnumApplicationSurveyResponsesDto;

  applicants.forEach((applicant) => {
    applicant.ProductQuestions.StateApplicationQuestion.forEach((question) => {
      const questionResponse =
        String(question.QuestionAnswer) === '[object Object]'
          ? ''
          : String(question.QuestionAnswer);
      retval.questions[question.QuestionID] = questionResponse;

      if (question.QuestionAnswerDetails) {
        question.QuestionAnswerDetails.QuestionAnswerDetail.forEach(
          (detail) => {
            detail.DetailFields.DetailField.forEach((field, index) => {
              const fieldName =
                String(field.DetailFieldName) === '[object Object]'
                  ? ''
                  : field.DetailFieldName;
              retval.question_answer_details = {
                ...retval.question_answer_details,
                [question.QuestionID]: {
                  ...(retval.question_answer_details[
                    question.QuestionID
                  ] as {}),
                  [`${index + 1}_${fieldName === '' ? '{}' : fieldName}`]:
                    field.DetailFieldValue,
                },
              };
            });
          }
        );
      }
    });
  });
  return retval;
};

export const getUnumParentQuestion = (
  questions: UnumApplicantDto[],
  name: string
) => {
  const temp = questions.find((question) =>
    question.ProductQuestions.StateApplicationQuestion.find(
      (stateQuestion) => stateQuestion.QuestionID === name
    )
  );
  return temp?.ProductQuestions.StateApplicationQuestion.find(
    (stateQuestion) => stateQuestion.QuestionID === name
  );
};

export const getUnumHeightValue = (
  value: string,
  currentValue: string,
  isHeight: string
) => {
  const tempValue = currentValue !== '' ? String(currentValue) : '0-0';
  return isHeight === 'feet'
    ? `${value}-${tempValue.split('-')[1]}`
    : `${tempValue.split('-')[0]}-${value}`;
};
